import React, { useState } from 'react';
import { Icons } from '../../../components/ui/Icons';
import { useReveal } from '../hooks/useScrollReveal';

const tabs = [
  {
    id: 'founders',
    label: 'Founders',
    tagline: 'Automate the ops. Focus on the vision.',
    cases: [
      {
        title: 'Investor updates on autopilot',
        desc: 'Pull metrics from Sheets, draft in Notion, email your investor list — one command.',
        apps: [Icons.GoogleSheets, (p: any) => <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className={p.className} />, Icons.Gmail],
        metric: 'Save 4+ hrs/week',
      },
      {
        title: 'Meeting prep, instantly',
        desc: 'Auto-generated briefs with attendee context, last touchpoints, and agenda items.',
        apps: [Icons.GoogleCalendar, Icons.Gmail],
        metric: 'Never go in blind',
      },
    ],
  },
  {
    id: 'freelancers',
    label: 'Freelancers',
    tagline: 'Bill for real work, not admin.',
    cases: [
      {
        title: 'Client communication hub',
        desc: 'Summarize client emails, auto-schedule follow-ups, track deliverables — all from chat.',
        apps: [Icons.Gmail, Icons.GoogleCalendar, Icons.GoogleDrive],
        metric: 'Zero missed deadlines',
      },
      {
        title: 'Invoicing flow',
        desc: 'Log hours in Sheets, generate invoices in Docs, email clients on schedule.',
        apps: [Icons.GoogleSheets, Icons.GoogleDocs, Icons.Gmail],
        metric: 'Get paid faster',
      },
    ],
  },
  {
    id: 'teams',
    label: 'Teams',
    tagline: 'Align everyone. Cut the overhead.',
    cases: [
      {
        title: 'Async standups',
        desc: 'Collect updates, compile into Notion, post a Slack digest every morning.',
        apps: [(p: any) => <img src="https://cdn-icons-png.flaticon.com/128/3800/3800024.png" alt="S" className={p.className} />, (p: any) => <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className={p.className} />],
        metric: 'Cut meetings by 60%',
      },
      {
        title: 'Cross-tool search',
        desc: 'Search Gmail, Drive, Notion, Slack from one interface. Find anything in seconds.',
        apps: [Icons.Gmail, Icons.GoogleDrive, (p: any) => <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className={p.className} />],
        metric: '10× faster lookups',
      },
    ],
  },
];

const LandingUseCases: React.FC = () => {
  const [active, setActive] = useState(0);
  const ref = useReveal();
  const t = tabs[active];

  return (
    <section className="relative py-10 md:py-14 bg-white overflow-hidden">
      <div className="section-line" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 pt-2">
        <div className="max-w-2xl mb-12 sr">
          <p className="text-[11px] font-semibold text-black/30 tracking-[0.2em] uppercase mb-4">Use Cases</p>
          <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.04em] text-black leading-[1.1]">
            Built for how<br />you actually work
          </h2>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-4 p-1 bg-black/[0.03] rounded-full w-fit sr delay-2">
          {tabs.map((tab, i) => (
            <button
              key={tab.id}
              onClick={() => setActive(i)}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-300 ${
                active === i ? 'bg-black text-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]' : 'text-black/40 hover:text-black/60'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tagline */}
        <p className="text-[15px] font-medium text-black/30 mb-10">{t.tagline}</p>

        {/* Cases */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {t.cases.map((c, i) => (
            <div
              key={`${t.id}-${i}`}
              className="bg-white rounded-2xl p-7 border border-black/[0.05] hover-lift animate-fade-in-up"
              style={{ animationDelay: `${i * 120}ms`, opacity: 0 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.5)]" />
                <span className="text-[11px] font-semibold text-emerald-600/60">{c.metric}</span>
              </div>
              <h3 className="text-[20px] md:text-[32px] font-medium text-black tracking-[-0.02em] mb-2">{c.title}</h3>
              <p className="text-[14px] text-black/40 font-normal leading-relaxed mb-5">{c.desc}</p>
              <div className="flex items-center gap-1.5 pt-4 border-t border-black/[0.04]">
                {c.apps.map((Ic, j) => (
                  <div key={j} className="w-7 h-7 bg-[#f8f8f8] rounded-lg flex items-center justify-center border border-black/[0.03]">
                    <Ic className="w-3.5 h-3.5 object-contain" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingUseCases;
